import React from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Accordion from 'react-bootstrap/Accordion';
import Table from 'react-bootstrap/Table';
import Card from 'react-bootstrap/Card';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Alert from 'react-bootstrap/Alert';

import PieChart from '../components/PieChart.jsx';
import LineChart from '../components/LineChart.jsx';
import MonthPicker from '../components/MonthPicker';
import timeHelper from '../helpers/time';
import 'bootstrap/dist/css/bootstrap.min.css';

const MS_JIRA_BASE_URL = 'https://makingscience.atlassian.net/browse';
const padTo2Digits = (num) => num.toString().padStart(2, '0');

const HeaderCard = ({ title, children }) => (
    <Card>
        <Card.Header>{title}</Card.Header>
        <Card.Body>
            {children}
        </Card.Body>
    </Card>
);

const AlertJiraLink = () => (
    <Alert variant={'info'}>
        <Alert.Link href="https://makingscience.atlassian.net/jira/dashboards/10000" target="_blank">
            Click here &nbsp;
        </Alert.Link>
        to go to Jira Dashboard.
    </Alert>
);

const AlertDataAsync = () => (
    <Alert variant={'info'}>
        All information is calculated each day at midnight. If you have just logged hours you may not see it reflected until tomorrow.
    </Alert>
);

const Tasks = ({ tasks }) => (
    <Table striped bordered hover>
        <thead>
            <tr>
                <th>#</th>
                <th>Project</th>
                <th>Issue Key</th>
                <th>Issue Name</th>
                <th>Issue type</th>
                <th>Time spend (Hours)</th>
            </tr>
        </thead>
        <tbody>
            {tasks.map(({ issueKey, issueName, issuetypeName, project, timeSpent }, index) => (
                <tr key={`table-item-${issueKey}-${index}`}>
                    <td>{index}</td>
                    <td>{project}</td>
                    <td>
                        <a href={`${MS_JIRA_BASE_URL}/${issueKey}`} target='_blank'>{issueKey}</a>
                    </td>
                    <td>{issueName}</td>
                    <td>{issuetypeName}</td>
                    <td>{timeHelper.secondsToHour(timeSpent)} h.</td>
                </tr>
            ))}
        </tbody>
    </Table>
)

const DayAccordion = ({ index, day }) => {
    const dateFormatted = new Date(day.dayStr);

    return (
        <Accordion.Item eventKey={index} key={`accordeon-item-${index}`}>
            <Accordion.Header>
                {timeHelper.getDayName(dateFormatted)} {padTo2Digits(index)} - Logged hours ({timeHelper.secondsToHour(day.totalTimeSpent)} h.)
            </Accordion.Header>
            <Accordion.Body>
                <Tasks tasks={day.tasks} />
            </Accordion.Body>
        </Accordion.Item>
    )
}

const DaysAccordion = ({ days, comparativeHoursData }) => {
    let daysRendered = [];
    
    if (Object.keys(days).length > 0) {
        const lastDayImputed = Math.max(...Object.keys(days));

        daysRendered = Array(lastDayImputed + 1)
            .fill(0)
            .map((_, index) => {
                if (days[index]) {
                    return (
                        <DayAccordion
                            key={`accordeon-day-${index}`}
                            day={days[index]}
                            index={index}
                        />
                    )
                }
            });
    }

    return (
        <Row>
            <Row>
                <Accordion defaultActiveKey="0">
                    <LineChart comparativeHoursData={comparativeHoursData} />

                    {daysRendered}
                </Accordion>
            </Row>
            <AlertJiraLink />
        </Row>
    )
}

const Dashboard = ({ data, projectsData }) => {
    return (
        <Row>
            <Row className='chart-dashboard'>
                <h1>Projects and logged hours</h1>
                <PieChart
                    data={projectsData}
                    downBarTitle={'Logged hours'}
                    downBarKey={'totalHours'}
                    totalTimeSpent={data.totalTimeSpent}
                />
            </Row>
            <AlertJiraLink />
        </Row>
    );
}

const ProjectTypeDashboard = ({ data }) => {
    return (
        <Row>
            <Row>
                <h1>Projects type logged hours</h1>
                <PieChart
                    data={data.projectTypes}
                    totalTimeSpent={data.totalTimeSpent}
                    downBarTitle={'Logged hours'}
                    downBarKey={'totalHours'}
                />
            </Row>
            <AlertJiraLink />
        </Row>
    );
}

const AppView = ({
    data,
    projectsData,
    yearSelected,
    monthSelected,
    onMonthChange,
    agreementHours,
    comparativeHoursData,
    isCurrentMonth,
}) => (
    <Container>
        <Row>
            <Col md={12}>
                <MonthPicker onMonthChange={onMonthChange} yearSelected={yearSelected} monthSelected={monthSelected} />
            </Col>
            <Col>
                <HeaderCard title={`${isCurrentMonth ? 'Actual hours logged until yesterday': 'Actual hours logged'}`}>
                    <Card.Title>
                        {timeHelper.secondsToHour(data.totalTimeSpent)} h.
                    </Card.Title>
                </HeaderCard>
            </Col>
            <Col>
                <HeaderCard title={`${isCurrentMonth ? 'Total capacity according to your contract until yesterday': 'Total capacity according to your contract'}`}>
                    <Card.Title>{agreementHours?.totalHours} h.</Card.Title>
                </HeaderCard>
            </Col>
        </Row>
        <AlertDataAsync />
        <br />
        <Tabs
            defaultActiveKey="dashboard"
            className="mb-3"
        >
            <Tab eventKey="dashboard" title="Logged hours by projects">
                <Dashboard projectsData={projectsData} data={data} />
            </Tab>
            <Tab eventKey="project_types" title="Logged hours by project type">
                <ProjectTypeDashboard data={data} />
            </Tab>
            <Tab eventKey="profile" title="Detail task per day">
                <DaysAccordion days={data.days} comparativeHoursData={comparativeHoursData} />
            </Tab>
        </Tabs>
    </Container>

)

const App = ({
    data,
    projectsData,
    yearSelected,
    monthSelected,
    onMonthChange,
    agreementHours,
    comparativeHoursData,
    isCurrentMonth,
}) => {
    return (
        <Container fluid>
            <Row>
                <Col>
                    <div className="App">
                        <AppView
                            projectsData={projectsData}
                            data={data}
                            yearSelected={yearSelected}
                            monthSelected={monthSelected}
                            onMonthChange={onMonthChange}
                            agreementHours={agreementHours}
                            comparativeHoursData={comparativeHoursData}
                            isCurrentMonth={isCurrentMonth}
                        />
                    </div>
                </Col>
            </Row>
        </Container>
    )
}

export default App;
